import { useCallback, useMemo } from 'react';
import { TableConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { useDisclosure } from '@mantine/hooks';

import { usePostTableRowData } from '../../hooks/usePostTableRowData';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { invalidateAllTableCaches } from '../../utils/queryCacheHelper';

import { CreateInvestmentLimitModalProps } from './CreateInvestmentLimitModal';

export type UseCreateInvestmentLimitModalPropertiesReturnValue = {
  createInvestmentLimitModalProps?: CreateInvestmentLimitModalProps;
  open: () => void;
};

function isInvestmentLimitRowConfigured(config: TableConfigDto) {
  return config.createRowConfig?.type === 'createInvestmentLimitRow';
}

export function useCreateInvestmentLimitModalProperties(
  contentId: string,
  config: TableConfigDto,
  instanceId: string
): UseCreateInvestmentLimitModalPropertiesReturnValue {
  const [isOpen, { open, close }] = useDisclosure();
  const { postTableRowData, reset } = usePostTableRowData();
  const isConfigured = isInvestmentLimitRowConfigured(config);
  const globalFilterSelections = useEffectiveGlobalSelections();
  const instanceFilterSelections = useSelections(instanceId);
  const pageFilterSelections = useMemo(
    () => ({ ...globalFilterSelections, ...instanceFilterSelections }),
    [globalFilterSelections, instanceFilterSelections]
  );
  const rowColumnKeys = useMemo(
    () => config.columns.map((column) => column.key),
    [config.columns]
  );

  const createInvestmentLimitModalProps = useMemo<
    CreateInvestmentLimitModalProps | undefined
  >(() => {
    if (!isOpen || !isConfigured) {
      return undefined;
    }

    return {
      contentId,
      opened: isOpen,
      dataLevels: config.createRowConfig?.dataLevels ?? [],
      rowColumnKeys,
      onClose: () => {
        reset();
        close();
      },
      onApply: async (data) => {
        await postTableRowData(contentId, [], {
          context: { pageFilterSelections },
          data,
        });
        void invalidateAllTableCaches(config.cacheKey);
      },
    };
  }, [
    close,
    config.cacheKey,
    config.createRowConfig,
    contentId,
    isConfigured,
    isOpen,
    pageFilterSelections,
    postTableRowData,
    reset,
    rowColumnKeys,
  ]);

  const openModal = useCallback(() => {
    if (isConfigured) {
      open();
    }
  }, [isConfigured, open]);

  return {
    open: openModal,
    createInvestmentLimitModalProps,
  };
}
